import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLock, FiShield, FiFileText, FiEyeOff } = FiIcons;

const PrivacyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-[calc(100vh-64px)] bg-slate-50 py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center px-4 py-2 rounded-full bg-indigo-50 text-indigo-700 text-[10px] font-black uppercase tracking-[0.3em] mb-6"
          >
            <SafeIcon icon={FiLock} className="mr-2" /> Data Protection
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-black text-slate-900 tracking-tighter mb-6"
          >
            Privacy <span className="text-indigo-600">Policy</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-slate-500 font-medium max-w-2xl mx-auto"
          >
            How SOHLocal collects, uses, and protects your personal information.
          </motion.p>
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.3 }}
          className="bg-white rounded-[3rem] shadow-2xl shadow-slate-200/50 p-8 md:p-16 border border-slate-100"
        >
          <div className="prose prose-slate max-w-none prose-headings:font-black prose-headings:tracking-tight prose-h2:text-2xl prose-h2:text-slate-900 prose-h2:mt-12 prose-h2:mb-6 prose-p:text-slate-600 prose-p:leading-relaxed prose-li:text-slate-600">
            
            <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-12 pb-8 border-b border-slate-100">
              Last Updated: May 2024
            </p>

            <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-6 mb-12 flex items-start">
              <SafeIcon icon={FiShield} className="text-indigo-600 text-2xl mr-4 flex-shrink-0 mt-1" />
              <p className="text-indigo-900 font-medium text-sm m-0">
                <strong>Your privacy is critically important to us.</strong> This Privacy Policy outlines how SOHLocal ("we," "our," or "the platform") handles your personal data when you use our marketplace to discover, buy, or sell local food.
              </p>
            </div>

            <h2>1. Information We Collect</h2>
            <p>
              To provide you with a seamless marketplace experience, we collect the following types of information:
            </p>
            <ul>
              <li><strong>Account Information:</strong> When you register, we collect your name, email address, phone number, and general location (city/neighborhood).</li>
              <li><strong>Seller Profile Data:</strong> If you register as a Seller, we collect your business name, description, operating hours, service area, and profile images to display publicly on the marketplace.</li>
              <li><strong>Communications:</strong> We securely store messages exchanged between Buyers and Sellers through our in-app chat system to facilitate orders and resolve issues.</li>
              <li><strong>Order History:</strong> We maintain records of your transaction history, including items ordered, quantities, and selected delivery/pickup methods.</li>
              <li><strong>Technical Data:</strong> We automatically collect standard technical information, such as IP addresses, browser types, and device identifiers, to ensure platform security and performance.</li>
            </ul>

            <h2>2. How We Use Your Information</h2>
            <p>
              We use the collected information strictly to operate and improve the SOHLocal platform:
            </p>
            <ul>
              <li>To connect Buyers with local Sellers in their area.</li>
              <li>To facilitate communication and order coordination between users.</li>
              <li>To provide customer support and respond to secure contact inquiries.</li>
              <li>To monitor platform security, investigate fraud, and enforce our Terms and Conditions.</li>
            </ul>

            <h2>3. Information Sharing and Disclosure</h2>
            <p>
              <strong>We do not sell your personal data to third parties.</strong> We only share your information under the following circumstances:
            </p>
            <ul>
              <li><strong>Between Users:</strong> When an order is placed or a chat is initiated, necessary details (such as your name and order specifics) are shared between the Buyer and Seller to allow for fulfillment.</li>
              <li><strong>Public Profiles:</strong> Seller business names, descriptions, locations, and menus are visible to the public to allow for marketplace discovery.</li>
              <li><strong>Service Providers:</strong> We share data with trusted third-party services (like secure database hosting and bot-protection tools) that help us operate the platform. These providers are bound by strict confidentiality agreements.</li>
              <li><strong>Legal Compliance:</strong> We may disclose information if required to do so by law, or in response to valid requests by public authorities.</li>
            </ul>

            <h2>4. Payment Information</h2>
            <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100 mb-6">
              <p className="text-sm font-bold m-0 text-slate-700 uppercase tracking-wide mb-2 flex items-center">
                <SafeIcon icon={FiEyeOff} className="mr-2 text-indigo-500" /> No Payment Data Stored
              </p>
              <p className="text-sm text-slate-600 m-0 leading-relaxed">
                SOHLocal does not process, handle, or store any credit card numbers, bank account details, or payment information. All financial transactions are conducted directly between the Buyer and the Seller outside of our platform.
              </p>
            </div>

            <h2>5. Data Security and Retention</h2>
            <p>
              We implement industry-standard security measures, including data encryption and secure server hosting, to protect your personal information from unauthorized access. We retain your personal data only for as long as your account is active or as needed to provide you with our services and comply with legal obligations.
            </p>

            <h2>6. Your Privacy Rights</h2>
            <p>
              Depending on your jurisdiction, you have the right to:
            </p>
            <ul>
              <li>Access the personal information we hold about you.</li>
              <li>Request corrections to inaccurate or incomplete data via your Profile Settings.</li>
              <li>Request the deletion of your account and associated personal data.</li>
              <li>Opt-out of non-essential communications.</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us through the Secure Contact page.
            </p>

            <h2>7. Children's Privacy</h2>
            <p>
              SOHLocal is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected personal data from a child, we will take immediate steps to delete that information.
            </p>

            <h2>8. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices or regulatory requirements. We will notify you of any material changes by posting the updated policy on this page and updating the "Last Updated" date.
            </p>

            <h2>9. Contact Us</h2>
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or how we handle your data, please reach out to our privacy team via the Secure Contact page or email us directly at privacy@sohlocal.com.
            </p>

          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPage;